import React, {PureComponent} from "react";
import {connect, DispatchProp} from "react-redux";
import {loadScene} from "../../store/routing/actions";
import {startLevel} from "../../store/game/actions";
import {IApplicationState} from "../../store";
import {LevelScreen} from "./LevelScreen";
import TopPanel from "../../shared/components/top-panel/TopPanel";
import {SceneryType} from "../../store/settings/SceneryType";
import {Random} from "../../shared/utils/random";
import {range} from "../../shared/utils/range";
import styles from "./level-selection.module.css";
import {Animation} from "gsap";
import playBackground from "../../shared/sounds/playBackground";
import {ILevelMapStats} from "../../store/game/types";
import {AnimationSettingsProp} from "../../store/settings/types";
import {showTutorial} from "../../store/tutorial/actions";
import { cssHelper } from "../../shared/utils/cssHelper";
import isTouchDevice from "../../shared/utils/isTouchDevice";
import scrollbar from "../../shared/styles/scrollbars.module.css";

interface LevelSelectionSceneProps {
    readonly levelsCompleted: number;
    readonly totalLevelScreens: number;
    readonly levelButtonsPerScreen: number;
    readonly seasons:  Array<SceneryType>;
    readonly levelStats: Record<number, ILevelMapStats>;
    readonly scaleFactor: number;
    readonly randomSeed: number;
}

class LevelSelectionScene extends PureComponent<LevelSelectionSceneProps & AnimationSettingsProp & DispatchProp> {
    private readonly scrollView: React.RefObject<HTMLDivElement>;
    private readonly currentLevelButton: React.RefObject<HTMLButtonElement>;
    private activeTween?: Animation;

    constructor(props: LevelSelectionSceneProps & AnimationSettingsProp & DispatchProp, context: any) {
        super(props, context);

        this.scrollView = React.createRef();
        this.currentLevelButton = React.createRef();
    }

    render() {
        const levelScreens = this.renderLevelScreens();

        return (
            <section className={styles.container}>
                <TopPanel animate={true} delay={0.2} onBackButtonClick={() => this.props.dispatch(loadScene.request("MainMenu"))} />

                <div className={cssHelper(styles.scroll_view).add(isTouchDevice() ? scrollbar.empty : scrollbar.thin).toString()} ref={this.scrollView}>
                    {levelScreens}
                </div>
            </section>
        );
    }

    componentDidMount(): void {
        playBackground("main-menu");

        if (!this.scrollView.current)
            return;

        const {animation} = this.props;

        if (this.currentLevelButton.current) {
            const boundingRect = this.currentLevelButton.current.getBoundingClientRect();

            this.scrollView.current.scrollTop = boundingRect.top / this.props.scaleFactor - this.scrollView.current.offsetHeight / 2;
        } else {
            this.scrollView.current.scrollTop = this.scrollView.current.scrollHeight;
        }

        this.props.dispatch(showTutorial.request({
            selector: `.${styles.level_current}`,
            title: "levelselection.firstleveltutor",
            delay: animation.enabled ? 1.0 : 0
        }));
    }

    componentWillUnmount(): void {
        this.activeTween && this.activeTween.kill();
    }

    private renderLevelScreens() {
        const currentLevel = this.props.levelsCompleted + 1;
        const currentScreenIndex = Math.floor(currentLevel  / this.props.levelButtonsPerScreen);
        const backShift = Math.floor(this.props.totalLevelScreens / 2);
        const firstScreenIndex = currentScreenIndex > backShift
            ? currentScreenIndex - backShift
            : 0;

        return range(this.props.totalLevelScreens, firstScreenIndex).map(screenIndex => {
            const scenery = this.getScreenSceneryType(screenIndex);
            const random = new Random(this.props.randomSeed ^ screenIndex);

            return <LevelScreen key={screenIndex}
                                random={random}
                                onLevelSelected={this.selectLevel.bind(this, scenery)}
                                screenIndex={screenIndex}
                                currentLevel={currentLevel}
                                levelButtonsPerScreen={this.props.levelButtonsPerScreen}
                                levelStats={this.props.levelStats}
                                scenery={scenery}
                                currentLevelButtonRef={this.currentLevelButton}
                                animation={this.props.animation}/>;
        }).reverse();
    }

    private getScreenSceneryType(index: number) {
        return this.props.seasons[index % this.props.seasons.length];
    }

    private selectLevel(scenery: SceneryType, level: number) {
        this.props.dispatch(startLevel({level, scenery}));
    }
}

export default connect(({ player, game, tutorial, settings }: IApplicationState) => ({
    levelsCompleted: player.levelsCompleted,
    seasons: settings.general.seasons,
    totalLevelScreens: settings.general.totalLevelScreens,
    levelButtonsPerScreen: settings.levelButtonsPerScreen,
    levelStats: game.levelMapProgress,
    scaleFactor: settings.canvas.scaleFactor,
    animation: settings.animation,
    randomSeed: settings.randomSeed,
}))(LevelSelectionScene);